import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { DatumLateralUsageType } from "building-types/datums/datumUsageTypes";
import type { FoundationEdgeOverridesTable } from "building-types/foundations/foundationSystemTypes";
import type { Table } from "common/data/table";
import { offsetPolyLine2VariableStep } from "geom/offset";
import { polyLine2Copy, polyLine2Move, polyLine2ReflectOverYAxis, polyLine2Reverse } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

/** Prefix for transition edge IDs created when offsetting foundation edges. These edges should not have labels. */
export const FOUNDATION_TRANSITION_EDGE_PREFIX = "foundation-transition-";

/**
 * Checks if an edge ID represents a transition edge (created when offsetting foundation edges).
 */
export function isFoundationTransitionEdge(edgeId: string): boolean {
  return edgeId.startsWith(FOUNDATION_TRANSITION_EDGE_PREFIX);
}

/**
 * Extracts the source edge ID from a transition edge ID.
 * Transition edges inherit their properties (assembly, datums, etc.) from the source edge.
 * Format: "foundation-transition-{sourceEdgeId}-{counter}"
 * Returns undefined if the edge ID is not a transition edge.
 */
export function getTransitionEdgeSourceId(edgeId: string): string | undefined {
  if (!isFoundationTransitionEdge(edgeId)) {
    return undefined;
  }
  // Format: "foundation-transition-{sourceEdgeId}-{counter}"
  const parts = edgeId.slice(FOUNDATION_TRANSITION_EDGE_PREFIX.length).split("-");
  if (parts.length >= 1) {
    return parts[0];
  }
  return undefined;
}

/**
 * Reflects a foundation perimeter over the mirror axis. Reflecting flips the winding, so the
 * traversal is reversed to restore it.
 */
export function mirrorFoundationPerimeter(perimeter: PolyLine2, mirrorTranslationX: number): PolyLine2 {
  const mirroredPerimeter = polyLine2ReflectOverYAxis(polyLine2Copy(perimeter));
  polyLine2Reverse(mirroredPerimeter);
  polyLine2Move(mirroredPerimeter, mirrorTranslationX, 0);
  return mirroredPerimeter;
}

export function foundationPerimeterInfoToUse(
  perimeter: PolyLine2,
  edgeIds: string[],
  foundationEdgeOverrides: FoundationEdgeOverridesTable | undefined,
  mirror: boolean,
  mirrorTranslationX: number,
  options?: {
    datumAbstractions?: Table<DatumAbstraction>;
    foundationLateralDatumId?: string;
  },
): { perimeter: PolyLine2; edgeIds: string[] } {
  let perimeterToUse = polyLine2Copy(perimeter);
  let edgeIdsToUse = [...edgeIds];
  const { datumAbstractions, foundationLateralDatumId } = options ?? {};

  const resolveLateralDatumOffset = (datumId: string | undefined): number | undefined => {
    if (!datumId || !datumAbstractions) {
      return undefined;
    }
    const datumAbstraction = datumAbstractions[datumId];
    if (!datumAbstraction?.isValidForUsage(DatumLateralUsageType.FOUNDATION_LATERAL)) {
      return undefined;
    }
    return datumAbstraction.resolveLateralDatum();
  };

  // Overrides are keyed by the edge's 1-based position in the `edgeIds` array they arrive in, so
  // resolve them up front -- before mirroring reorders the edges -- and then carry the resolved
  // offsets through the same reordering as the perimeter. Reading the offset off the array position
  // after the reversal is what used to land it on the wrong edge.
  let offsets: number[] = edgeIdsToUse.map((_edgeId, index) => {
    const edgeOverride = foundationEdgeOverrides?.[index + 1];
    const edgeOffset = edgeOverride?.offset ?? 0;
    const edgeLateralOffset = resolveLateralDatumOffset(edgeOverride?.lateralDatumId);
    const fallbackLateralOffset = resolveLateralDatumOffset(foundationLateralDatumId) ?? 0;
    const lateralOffset = edgeLateralOffset ?? fallbackLateralOffset;
    return edgeOffset + lateralOffset;
  });

  if (mirror && perimeterToUse) {
    perimeterToUse = mirrorFoundationPerimeter(perimeterToUse, mirrorTranslationX);
    // Reflecting and reversing sends edge `i` to position `edgeCount - 1 - i`, for closed loops and
    // open paths alike, so a plain reverse keeps ids and offsets naming the edge at each position.
    edgeIdsToUse = edgeIdsToUse.slice().reverse();
    offsets = offsets.slice().reverse();
  }

  if (perimeterToUse && edgeIdsToUse.length > 0 && offsets.some((v) => v !== 0)) {
    const { pl, edgeMapping } = offsetPolyLine2VariableStep(perimeterToUse, offsets, true);
    perimeterToUse = pl;

    // Update edgeIds to reflect the new edge positions after offset creates transition edges.
    // edgeMapping maps new edge indices to original edge indices (undefined for new transition edges).
    // Transition edges get unique IDs that include the source edge ID they inherit properties from.
    // Format: "foundation-transition-{sourceEdgeId}-{counter}"
    let transitionCounter = 0;
    const updatedEdgeIds: string[] = edgeMapping.map((originalIndex, newIndex) => {
      if (originalIndex !== undefined) {
        return edgeIdsToUse[originalIndex];
      }

      // For transition edges, find the adjacent original edge with the non-zero offset.
      // This edge's properties (assembly, datums, etc.) should be inherited by the transition.
      let sourceEdgeId: string | undefined;

      // Look at adjacent edges in the mapping to find which original edge this transition connects to
      // Check the previous mapped edge first
      for (let i = newIndex - 1; i >= 0; i--) {
        const prevOriginal = edgeMapping[i];
        if (prevOriginal !== undefined && offsets[prevOriginal] !== 0) {
          sourceEdgeId = edgeIdsToUse[prevOriginal];
          break;
        }
      }

      // If no offset edge found before, check after
      if (!sourceEdgeId) {
        for (let i = newIndex + 1; i < edgeMapping.length; i++) {
          const nextOriginal = edgeMapping[i];
          if (nextOriginal !== undefined && offsets[nextOriginal] !== 0) {
            sourceEdgeId = edgeIdsToUse[nextOriginal];
            break;
          }
        }
      }

      // Fallback to the nearest original edge if no offset edge found
      if (!sourceEdgeId) {
        for (let i = newIndex - 1; i >= 0; i--) {
          const mappedIndex = edgeMapping[i];
          if (mappedIndex !== undefined) {
            sourceEdgeId = edgeIdsToUse[mappedIndex];
            break;
          }
        }
      }

      return `${FOUNDATION_TRANSITION_EDGE_PREFIX}${sourceEdgeId ?? "unknown"}-${transitionCounter++}`;
    });
    edgeIdsToUse = updatedEdgeIds;
  }

  return { perimeter: perimeterToUse, edgeIds: edgeIdsToUse };
}
